import { useABAC } from '@adornis/abac/client/useABAC.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import type { Client } from '@nsc/clients/entities/Client.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { html } from 'lit';
import { subscribeAccessibleClients } from '../../api/nexus-scoped-clients.js';
import { accessController } from '../../features/permissions/init-permissions.js';
import { NEXUS_DASHBOARD_COLUMN_CLASS } from './DashboardClassic.js';

const OverviewClientCard = stateful((props: { client: Client }) => {
  const c = props.client;
  const id = String(c._id);
  const { can, loading } = useABAC(accessController, {
    type: '@adornis/abac/simple-role',
    permission: NexusPermission.ClientKillSwitch,
    context: id,
  });

  const toggle = () => {
    if (!can || loading) return;
    c.enabled = !c.enabled;
    void c.save?.();
  };

  return html`
    <section class="card bg-primary/5 shadow-2xl rounded-2xl border border-primary/20">
      <div class="card-body py-5 flex flex-row items-center justify-between gap-3 min-w-0">
        <a
          class="flex-1 min-w-0 flex flex-col gap-1 no-underline rounded-xl -m-1 p-1 hover:bg-base-100/40 transition-colors"
          href="/clients/${c._id}"
        >
          <span class="font-semibold text-lg text-base-content truncate">${c.name}</span>
          <span class="text-xs text-base-content/60">${c.workflowType ?? '—'}</span>
        </a>
        <input
          type="checkbox"
          class="toggle toggle-primary scale-110 shrink-0"
          .checked=${c.enabled ?? false}
          ?disabled=${!can || loading}
          @change=${toggle}
          aria-label="Nexus on or off for ${c.name ?? 'client'}"
        />
      </div>
    </section>
  `;
});

export const DashboardClientOverview = stateful(() => {
  const clients = useObservable(
    () =>
      subscribeAccessibleClients()({
        _id: 1,
        name: 1,
        enabled: 1,
        workflowType: 1,
      }),
    [],
  );

  const list: Client[] = Array.isArray(clients) ? clients : [];

  return html`
    <section class="${NEXUS_DASHBOARD_COLUMN_CLASS} pt-6 pb-2 flex flex-col gap-4 min-w-0">
      <header class="min-w-0">
        <h1 class="text-2xl font-bold">Client overview</h1>
        <p class="text-sm text-base-content/70 mt-1">
          Start or stop Nexus for each client you can access. Use the switches where your role allows.
        </p>
      </header>
      ${list.length === 0
        ? html`<div class="alert alert-info">No clients available for your access.</div>`
        : html`<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${list.map(c => OverviewClientCard({ client: c }))}
          </div>`}
    </section>
  `;
});
